import { useState, useRef } from 'react';
import { Search, ChevronDown, ChevronUp, Printer, FileText, CheckCircle, AlertTriangle, XCircle, Star, TrendingUp, Camera, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { gerarHtmlContrato } from '../utils/gerarContratoCompraVenda';
import CapturaFoto from '../components/CapturaFoto';
import FotoMiniatura from '../components/FotoMiniatura';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const FAIXA_RENDA = [
  { value: 'ate_1sm',    label: 'Até 1 salário mínimo' },
  { value: '1_a_3sm',   label: '1 a 3 salários mínimos' },
  { value: '3_a_5sm',   label: '3 a 5 salários mínimos' },
  { value: 'acima_5sm', label: 'Acima de 5 salários mínimos' },
];

const TEMPO_CLIENTE = [
  { value: 'novo',         label: 'Primeira compra' },
  { value: 'ate_1ano',    label: 'Menos de 1 ano' },
  { value: '1_a_3anos',   label: '1 a 3 anos' },
  { value: 'acima_3anos', label: 'Mais de 3 anos' },
];

// Etapa 6 — espaços fixos de foto; todos opcionais (o contrato imprime sem fotos)
const SLOTS_FOTO = [
  { key: 'rosto',      label: '1. Rosto do cliente' },
  { key: 'doc_frente', label: '2. Documento de identidade (frente)' },
  { key: 'doc_verso',  label: '3. Documento de identidade (verso)' },
  { key: 'outro',      label: '4. Outro (ex.: comprovante de residência)' },
];

const FOTOS_VAZIAS = { rosto: null, doc_frente: null, doc_verso: null, outro: null };

const LABEL_SLOT = Object.fromEntries(SLOTS_FOTO.map(s => [s.key, s.label.replace(/^\d+\.\s*/, '')]));

// Abre a foto em tamanho real numa nova aba
async function abrirFotoAnterior(arquivo) {
  try {
    const resp = await apiService.analiseCredito.getFoto(arquivo);
    const url = URL.createObjectURL(resp.data);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch {
    alert('Não foi possível abrir a foto.');
  }
}

function cpfDigitos(cliente) {
  return String(cliente?.CPF || cliente?.CNPJ || '').replace(/\D/g, '');
}

function scoreColor(score) {
  if (score >= 70) return 'text-green-700';
  if (score >= 40) return 'text-amber-700';
  return 'text-red-700';
}

function scoreBg(score) {
  if (score >= 70) return 'bg-green-50 border-green-400';
  if (score >= 40) return 'bg-amber-50 border-amber-400';
  return 'bg-red-50 border-red-400';
}

function ResultBadge({ resultado }) {
  if (resultado === 'aprovado')
    return <span className="inline-flex items-center gap-1 text-xs font-medium bg-green-100 text-green-800 px-2 py-0.5 rounded"><CheckCircle size={12} />Aprovado</span>;
  if (resultado === 'aprovado_ressalvas')
    return <span className="inline-flex items-center gap-1 text-xs font-medium bg-amber-100 text-amber-800 px-2 py-0.5 rounded"><AlertTriangle size={12} />Aprovado com ressalvas</span>;
  return <span className="inline-flex items-center gap-1 text-xs font-medium bg-red-100 text-red-800 px-2 py-0.5 rounded"><XCircle size={12} />Reprovado</span>;
}

function ScoreBar({ value, max, color }) {
  const pct = Math.round((value / max) * 100);
  return (
    <div className="w-full bg-gray-100 rounded-full h-2">
      <div className={`h-2 rounded-full ${color}`} style={{ width: `${pct}%`, transition: 'width .4s' }} />
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function AnaliseCreditoPage() {
  const printRef = useRef();
  const { usuario } = useAuth();

  // Etapas: 'busca' | 'historico' | 'spc' | 'formulario' | 'resultado' | 'fotos'
  const [etapa, setEtapa]           = useState('busca');
  const [buscaTermo, setBuscaTermo] = useState('');
  const [clientes, setClientes]     = useState([]);
  const [loadingBusca, setLoadingBusca] = useState(false);
  const [erroBusca, setErroBusca]   = useState('');

  const [clienteSel, setClienteSel] = useState(null);
  const [historico, setHistorico]   = useState(null);
  const [loadingHist, setLoadingHist] = useState(false);

  const [form, setForm] = useState({
    renda_mensal:     '',
    tempo_cliente:    'ate_1ano',
    valor_solicitado: '',
    num_parcelas:     '1',
    valor_entrada:    '0',
    nome_operador:    usuario?.nome_completo || usuario?.username || '',
    observacoes:      '',
  });

  const [resultado, setResultado] = useState(null);
  const [salvando, setSalvando]   = useState(false);
  const [analiseId, setAnaliseId] = useState(null);

  // Análises anteriores do cliente
  const [analisesPrev, setAnalisesPrev] = useState([]);
  const [mostrarPrev, setMostrarPrev]   = useState(false);
  // Fotos da câmera de análises anteriores do mesmo CPF/CNPJ
  const [imagensPrev, setImagensPrev]           = useState([]);
  const [modalImagensPrev, setModalImagensPrev] = useState(false);
  const [modalPontos, setModalPontos] = useState(false);
  const navigate = useNavigate();
  

  // Cliente manual
  const [modoManual, setModoManual]     = useState(false);
  // Cidade/UF vêm preenchidas com a cidade da loja (maioria dos clientes)
  const DADOS_MANUAIS_VAZIOS = { nome: '', cpf: '', fone: '', endereco: '', cidade: 'Quixeramobim', uf: 'CE', mae: '' };
  const [dadosManuais, setDadosManuais] = useState(DADOS_MANUAIS_VAZIOS);
  const [cpfJaCadastrado, setCpfJaCadastrado] = useState(null);
  const [verificandoCpf, setVerificandoCpf]   = useState(false);

  // Etapa SPC
  const [spc, setSpc] = useState({
    situacao:             'nao_consultado',
    renda_mensal:         '',
    fonte_renda:          'declarado',
    observacoes:          '',
    doc_comprovante:      null,
    doc_comprovante_path: null,
    doc_comprovante_nome: null,
  });
  const [uploadandoDocs, setUploadandoDocs] = useState(false);
  // LGPD art. 9º: operador confirma que informou o cliente antes da coleta
  const [cienciaLgpd, setCienciaLgpd] = useState(false);

  // Etapa fotos: slot → { path, url } (url = prévia local)
  const [fotos, setFotos]               = useState(FOTOS_VAZIAS);
  const [slotCaptura, setSlotCaptura]   = useState(null);
  const [removendoSlot, setRemovendoSlot] = useState(null);

  async function enviarFoto(blob) {
    try {
      const resp = await apiService.analiseCredito.enviarFoto(analiseId, slotCaptura, blob);
      const url = URL.createObjectURL(blob);
      setFotos(f => {
        if (f[slotCaptura]?.url) URL.revokeObjectURL(f[slotCaptura].url);
        return { ...f, [slotCaptura]: { path: resp.data.data.path, url } };
      });
      setSlotCaptura(null);
    } catch {
      alert('Erro ao salvar a foto. Tente novamente.');
    }
  }

  async function removerFoto(slot) {
    if (!confirm('Remover esta foto?')) return;
    setRemovendoSlot(slot);
    try {
      await apiService.analiseCredito.removerFoto(analiseId, slot);
      setFotos(f => {
        if (f[slot]?.url) URL.revokeObjectURL(f[slot].url);
        return { ...f, [slot]: null };
      });
    } catch {
      alert('Erro ao remover a foto.');
    } finally {
      setRemovendoSlot(null);
    }
  }

  // ── Cliente manual ───────────────────────────────────────────────────────
  async function verificarCpfExistente(cpf) {
    const digits = cpf.replace(/\D/g, '');
    if (digits.length < 11) { setCpfJaCadastrado(null); return; }
    setVerificandoCpf(true);
    setCpfJaCadastrado(null);
    try {
      const resp = await apiService.clientes.buscar(cpf);
      const lista = resp.data?.data ?? resp.data ?? [];
      const encontrado = lista.find(c => c.CPF && c.CPF.replace(/\D/g, '') === digits);
      setCpfJaCadastrado(encontrado ?? null);
    } catch {
      // silencia erro de verificação
    } finally {
      setVerificandoCpf(false);
    }
  }

  function confirmarClienteManual() {
    // Nome da mãe é opcional: deixou de ser exigido para cadastro no SPC/Serasa
    if (!dadosManuais.nome.trim() || !dadosManuais.cpf.trim() || !dadosManuais.fone.trim() || !dadosManuais.endereco.trim()
        || !dadosManuais.cidade.trim() || !dadosManuais.uf.trim()) {
      alert('Preencha todos os campos obrigatórios: Nome, CPF, Telefone, Endereço, Cidade e UF.');
      return;
    }
    if (cpfJaCadastrado) return;
    setClienteSel({
      Ordem:    null,
      Codigo:   null,
      Nome:     dadosManuais.nome.trim(),
      Fone_1:   dadosManuais.fone.trim(),
      CPF:      dadosManuais.cpf.trim(),
      Endereco: dadosManuais.endereco.trim(),
      Cidade:   dadosManuais.cidade.trim(),
      Estado:   dadosManuais.uf.trim().toUpperCase(),
      Mae:      dadosManuais.mae.trim(),
      _manual:  true,
    });
    setHistorico({
      total_parcelas: 0, pagas_em_dia: 0, atraso_leve: 0,
      atraso_grave: 0, parcelas_abertas: 0, maior_atraso: 0,
      media_atraso: 0, pontuacao: 0, cliente_fiel: false,
    });
    setAnalisesPrev([]);
    carregarImagensPrev({ CPF: dadosManuais.cpf });
    setEtapa('historico');
  }

  // ── Modal confirmação WhatsApp AnaliseCredito ────────────────────────────────────────────
  const [modalZap, setModalZap]                   = useState(false);
  const [zapTelefone, setZapTelefone]             = useState('');
  const [zapEnviando, setZapEnviando]             = useState(false);
  const [zapEnviado, setZapEnviado]               = useState(false);
  const [zapErro, setZapErro]                     = useState('');
  const [zapResposta, setZapResposta]             = useState('');
  const [zapRespostaGravada, setZapRespostaGravada] = useState(false);
  const [zapGravando, setZapGravando]             = useState(false);

  // ── Forçar liberação (análise reprovada) ──
  const [modalLiberacao, setModalLiberacao]       = useState(false);
  const [motivoLiberacao, setMotivoLiberacao]     = useState('');
  const [liberacaoEnviando, setLiberacaoEnviando] = useState(false);
  const [liberacaoErro, setLiberacaoErro]         = useState('');
  const pollingRef = useRef(null);


  // ── Buscar clientes ──────────────────────────────────────────────────────
  async function buscarClientes(e) {
    e.preventDefault();
    if (!buscaTermo.trim()) return;
    setLoadingBusca(true);
    setErroBusca('');
    setClientes([]);
    try {
      const resp = await apiService.clientes.buscar(buscaTermo);
      const lista = resp.data?.data ?? resp.data ?? [];
      setClientes(lista);
      if (lista.length === 0) setErroBusca('Nenhum cliente encontrado.');
    } catch {
      setErroBusca('Erro ao buscar clientes.');
    } finally {
      setLoadingBusca(false);
    }
  }

  // ── Imagens de cadastros anteriores (pasta do CPF) ──────────────────────
  async function carregarImagensPrev(cliente) {
    setImagensPrev([]);
    const cpf = cpfDigitos(cliente);
    if (cpf.length !== 11 && cpf.length !== 14) return;
    try {
      const resp = await apiService.analiseCredito.listarImagensCpf(cpf);
      setImagensPrev(resp.data.data ?? []);
    } catch {
      // sem aviso se a consulta falhar
    }
  }

  // ── Selecionar cliente ───────────────────────────────────────────────────
  async function selecionarCliente(c) {
    setClienteSel(c);
    setEtapa('historico');
    setLoadingHist(true);
    setHistorico(null);
    setAnalisesPrev([]);
    carregarImagensPrev(c);
    try {
      const [hResp, aResp] = await Promise.all([
        apiService.analiseCredito.buscarHistorico(c.Ordem),
        apiService.analiseCredito.buscarPorCliente(c.Ordem),
      ]);
      setHistorico(hResp.data.data);
      setAnalisesPrev(aResp.data.data ?? []);
    } catch {
      setHistorico({ erro: true });
    } finally {
      setLoadingHist(false);
    }
  }

  // ── Upload de documentos ─────────────────────────────────────────────────
  async function fazerUploadDocs() {
    if (!spc.doc_comprovante) return;

    setUploadandoDocs(true);
    try {
      const fd = new FormData();
      // cpf antes dos arquivos: o backend usa para escolher a pasta do cliente
      const cpf = cpfDigitos(clienteSel);
      if (cpf) fd.append('cpf', cpf);
      fd.append('doc_comprovante_renda', spc.doc_comprovante);

      const resp = await apiService.analiseCredito.uploadDocs(fd);
      const d = resp.data.data;

      setSpc(s => ({
        ...s,
        doc_comprovante_path: d.doc_comprovante_renda?.path ?? s.doc_comprovante_path,
        doc_comprovante_nome: d.doc_comprovante_renda?.nome ?? s.doc_comprovante_nome,
      }));
    } catch {
      alert('Erro ao enviar o comprovante de renda. Verifique e tente novamente.');
    } finally {
      setUploadandoDocs(false);
    }
  }

  // ── Score ────────────────────────────────────────────────────────────────
  // Cliente sem histórico no ERP (0 parcelas): renda e entrada valem mais,
  // ocupando os 65 pts do histórico. Mesmas faixas do backend
  // (analiseCreditoService.js → pontosRendaSemHistorico / pontosEntradaSemHistorico).
  const semHistorico = !historico?.total_parcelas;
  const maxForm      = semHistorico ? 65 : 35;
  const maxEntrada   = semHistorico ? 35 : 20;

  function calcFormScore() {
    const renda = parseFloat(form.renda_mensal || spc.renda_mensal) || 0;
    const SM = 1518;
    const pts = semHistorico ? [50, 35, 20, 5] : [20, 17, 10, 4];
    let ptRenda = 0;
    if (renda >= 5 * SM)      ptRenda = pts[0];
    else if (renda >= 3 * SM) ptRenda = pts[1];
    else if (renda >= 1 * SM) ptRenda = pts[2];
    else if (renda > 0)       ptRenda = pts[3];
    const tempo = { novo: 0, ate_1ano: 5, '1_a_3anos': 10, acima_3anos: 15 };
    return ptRenda + (tempo[form.tempo_cliente] ?? 0);
  }

  // [percentual mínimo, pontos], da maior faixa para a menor
  const FAIXAS_ENTRADA = semHistorico
    ? [[0.50, 35], [0.30, 25], [0.20, 15], [0.10, 8], [0.05, 3]]
    : [[0.50, 20], [0.30, 15], [0.20, 10], [0.10, 5], [0.05, 2]];

  function calcEntradaScore() {
    const vs = parseFloat(form.valor_solicitado) || 0;
    const ve = parseFloat(form.valor_entrada) || 0;
    if (vs <= 0 || ve <= 0) return 0;
    const pct = ve / vs;
    return FAIXAS_ENTRADA.find(([min]) => pct >= min)?.[1] ?? 0;
  }

  function scoreTotal() {
    return Math.min(100, (historico?.pontuacao ?? 0) + calcFormScore() + calcEntradaScore());
  }

  function resultadoComEntrada() {
    if (historico?.atraso_critico) return 'reprovado';
    const score = scoreTotal();
    if (score >= 70) return 'aprovado';
    if (score >= 40) return 'aprovado_ressalvas';
    return 'reprovado';
  }

  // Menor entrada (%) cujos pontos cobrem o que falta para 70
  function entradaMinimaNecessaria() {
    const baseScore = (historico?.pontuacao ?? 0) + calcFormScore();
    if (baseScore >= 70) return null;
    const faltam = 70 - baseScore;
    const faixa = [...FAIXAS_ENTRADA].reverse().find(([, pts]) => pts >= faltam);
    return faixa ? Math.round(faixa[0] * 100) : null;
  }

  // ── Salvar análise ───────────────────────────────────────────────────────
  async function salvar() {
    if (!form.valor_solicitado || !form.nome_operador) {
      alert('Preencha o valor solicitado e o nome do operador.');
      return;
    }
    setSalvando(true);
    try {
      const resp = await apiService.analiseCredito.salvar({
        ordem_cliente:    clienteSel.Ordem   ?? 0,
        codigo_cliente:   clienteSel.Codigo  ?? 0,
        nome_cliente:     clienteSel.Nome,
        cpf_cliente:      cpfDigitos(clienteSel) || null,
        // Snapshot do cadastro: a reimpressão do contrato usa estes dados
        cliente_fone:     clienteSel.Fone_1 || clienteSel.Fone_2 || null,
        cliente_endereco: [clienteSel.Endereco, clienteSel.Numero, clienteSel.Bairro].filter(Boolean).join(', ') || null,
        cliente_cidade:   clienteSel.Cidade || null,
        cliente_uf:       clienteSel.Estado || null,
        cliente_mae:      clienteSel.Mae || null,
        ciencia_lgpd:     cienciaLgpd,
        historico,
        faixa_renda:      parseFloat(form.renda_mensal || spc.renda_mensal) || 0,
        tempo_cliente:    form.tempo_cliente,
        valor_solicitado: parseFloat(form.valor_solicitado),
        valor_aprovado:   parseFloat(form.valor_solicitado),
        num_parcelas:     parseInt(form.num_parcelas),
        valor_entrada:    parseFloat(form.valor_entrada || 0),
        nome_operador:    form.nome_operador,
        observacoes:      form.observacoes,
        // SPC
        spc_situacao:               spc.situacao,
        spc_renda_mensal:           spc.renda_mensal ? parseFloat(spc.renda_mensal) : null,
        spc_fonte_renda:            spc.fonte_renda,
        spc_observacoes:            spc.observacoes || null,
        doc_comprovante_renda_path: spc.doc_comprovante_path,
        doc_comprovante_renda_nome: spc.doc_comprovante_nome,
      });
      setResultado(resp.data.data);
      setAnaliseId(resp.data.data.id);
      setEtapa('resultado');
    } catch {
      alert('Erro ao salvar análise. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  }

  // ── Imprimir ─────────────────────────────────────────────────────────────
  function imprimir() {
    const conteudo = printRef.current?.innerHTML;
    if (!conteudo) return;
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>Análise de Crédito</title>
      <style>
        body{font-family:Arial,sans-serif;font-size:13px;margin:30px;color:#111}
        h2{margin-bottom:4px}h3{margin:14px 0 6px;border-bottom:1px solid #ccc;padding-bottom:4px}
        table{width:100%;border-collapse:collapse;margin-bottom:12px}
        td,th{padding:5px 8px;border:0.5px solid #ccc;text-align:left}
        th{background:#f5f5f5;font-weight:600}
        .score{font-size:32px;font-weight:700}
        .result{padding:8px 14px;border-radius:6px;font-weight:600;display:inline-block;margin:8px 0}
        .aprovado{background:#d1fae5;color:#065f46}
        .ressalvas{background:#fef3c7;color:#92400e}
        .reprovado{background:#fee2e2;color:#991b1b}
        .sig{border-top:1px solid #000;margin-top:40px;width:260px;text-align:center;padding-top:6px;font-size:12px}
        @media print{button{display:none}}
      </style></head><body>${conteudo}</body></html>
    `);
    win.document.close();
    win.print();
  }

  
  // ── Imprimir contrato de compra e venda ───────────────────────────────────
  function imprimirContrato() {
    const html = gerarHtmlContrato({
      cliente: clienteSel,
      dataContrato: new Date(),
      valorTotal: Number(form.valor_solicitado),
      numParcelas: Number(form.num_parcelas) || 1,
      valorEntrada: Number(form.valor_entrada) || 0,
      nomeOperador: form.nome_operador,
    });
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
  }

  // ── WhatsApp confirmação ─────────────────────────────────────────────────
  function abrirModalZap() {
    const tel = (clienteSel?.Fone_1 || clienteSel?.Fone_2 || '').replace(/\D/g, '');
    setZapTelefone(tel);
    setZapEnviado(false);
    setZapErro('');
    setZapResposta('');
    setZapRespostaGravada(false);
    setModalZap(true);
  }

  function mensagemZap() {
    const valor = Number(form.valor_solicitado).toLocaleString('pt-BR', {
      style: 'currency', currency: 'BRL',
    });
    return (
      `Olá! 👋\n\n` +
      `Uma compra de *${valor}* está sendo feita no seu nome *(${clienteSel?.Nome ?? ''})* em nossa loja.\n\n` +
      `Você confirma que será responsável pelo pagamento?\n\n` +
      `_Dantas Eletro — Quixeramobim_`
    );
  }

  async function enviarZap() {
    if (!zapTelefone) { setZapErro('Informe o telefone.'); return; }
    setZapEnviando(true);
    setZapErro('');
    try {
      let num = zapTelefone.replace(/\D/g, '');
      if (!num.startsWith('55')) num = '55' + num;
      if (num.length === 13) num = num.slice(0, 4) + num.slice(5);
      await apiService.whatsapp.enviarMensagem(num, mensagemZap());
      await apiService.analiseCredito.atualizarConfirmacaoZap(analiseId, zapTelefone, null);
      setZapEnviado(true);

      // ── Iniciar polling de resposta ──────────────────────────────────────
      if (pollingRef.current) clearInterval(pollingRef.current);
      pollingRef.current = setInterval(async () => {
        try {
          const resp = await apiService.whatsapp.buscarMensagensRecebidas(zapTelefone);
          const data = resp.data;
          if (data?.found && !zapRespostaGravada) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            setZapResposta(data.texto);
            // Gravar automaticamente
            await apiService.analiseCredito.atualizarConfirmacaoZap(
              analiseId, zapTelefone, data.texto
            );
            await apiService.whatsapp.marcarMensagemLida(zapTelefone);
            setZapRespostaGravada(true);
          }
        } catch { /* silencioso */ }
      }, 5000);

    } catch (e) {
      setZapErro('Erro ao enviar. Verifique se o WhatsApp está conectado.');
    } finally {
      setZapEnviando(false);
    }
  }

  async function gravarResposta() {
    if (!zapResposta.trim()) return;
    setZapGravando(true);
    try {
      await apiService.analiseCredito.atualizarConfirmacaoZap(analiseId, zapTelefone, zapResposta.trim());
      setZapRespostaGravada(true);
    } catch {
      alert('Erro ao gravar resposta.');
    } finally {
      setZapGravando(false);
    }
  }

  // ── Forçar liberação (análise reprovada) ───────────────────────────────────
  function abrirModalLiberacao() {
    setMotivoLiberacao('');
    setLiberacaoErro('');
    setModalLiberacao(true);
  }

  async function confirmarForcarLiberacao() {
    if (!motivoLiberacao.trim()) {
      setLiberacaoErro('Informe a justificativa da liberação.');
      return;
    }
    setLiberacaoEnviando(true);
    setLiberacaoErro('');
    try {
      await apiService.analiseCredito.forcarLiberacao(
        analiseId,
        motivoLiberacao.trim(),
        usuario?.nome_completo || usuario?.username || null
      );
      setModalLiberacao(false);
      setResultado(r => ({ ...r, liberacao_forcada: true }));
      setEtapa('fotos');
    } catch {
      setLiberacaoErro('Erro ao registrar a liberação. Tente novamente.');
    } finally {
      setLiberacaoEnviando(false);
    }
  }



  function resetarAnalise() {
    setEtapa('busca');
    setClientes([]);
    setBuscaTermo('');
    setResultado(null);
    setClienteSel(null);
    setHistorico(null);
    setAnalisesPrev([]);
    setImagensPrev([]);
    setModalImagensPrev(false);
    setModoManual(false);
    setDadosManuais(DADOS_MANUAIS_VAZIOS);
    setCpfJaCadastrado(null);
    setForm({ renda_mensal: '', tempo_cliente: 'ate_1ano', valor_solicitado: '', num_parcelas: '1', valor_entrada: '0', nome_operador: usuario?.nome_completo || usuario?.username || '', observacoes: '' });
    setSpc({ situacao: 'nao_consultado', renda_mensal: '', fonte_renda: 'declarado', observacoes: '', doc_comprovante: null, doc_comprovante_path: null, doc_comprovante_nome: null });
    Object.values(fotos).forEach(f => f?.url && URL.revokeObjectURL(f.url));
    setFotos(FOTOS_VAZIAS);
    setSlotCaptura(null);
    setCienciaLgpd(false);
  }

  // ─── Stepper ──────────────────────────────────────────────────────────────
  const steps = [
    { key: 'busca',      label: '1. Cliente' },
    { key: 'historico',  label: '2. Histórico' },
    { key: 'spc',        label: '3. SPC' },
    { key: 'formulario', label: '4. Formulário' },
    { key: 'resultado',  label: '5. Resultado' },
    { key: 'fotos',      label: '6. Fotos' },
  ];
  const stepOrder = ['busca', 'historico', 'spc', 'formulario', 'resultado', 'fotos'];
  const stepIdx   = stepOrder.indexOf(etapa);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-3xl mx-auto">

      {/* Modal explicação do sistema de pontos — ATUALIZADO */}
      {modalPontos && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 relative">
            <button onClick={() => setModalPontos(false)}
              className="absolute top-3 right-4 text-gray-400 hover:text-gray-700 text-xl font-bold">×</button>
            <h2 className="text-base font-semibold text-gray-900 mb-1">Como funciona o sistema de pontos</h2>
            <p className="text-xs text-gray-500 mb-4">A pontuação total vai de 0 a 100 e determina se o crediário é aprovado.</p>

            <div className="space-y-4 text-sm">

              {/* Histórico ERP */}
              <div>
                <p className="font-medium text-gray-700 mb-2">
                  📋 Histórico no ERP
                  <span className="text-xs font-normal text-gray-400 ml-1">(até 65 pts)</span>
                </p>
                <div className="bg-gray-50 rounded-lg divide-y divide-gray-100 text-xs">
                  {[
                    ['Parcelas pagas em dia',   'Proporcional ao % pago pontualmente — quanto maior, melhor',          '35'],
                    ['Média de atraso',         'Penaliza quanto maior for a média de dias em atraso (linear até 60d)', '15'],
                    ['Parcelas em aberto',      'Perde todos os pontos se houver dívidas abertas no momento',           '10'],
                    ['Maior atraso registrado', 'Penaliza histórico de atrasos graves (linear até 90 dias)',            '5'],
                  ].map(([item, desc, pts]) => (
                    <div key={item} className="flex justify-between items-start px-3 py-2 gap-2">
                      <div>
                        <div className="font-medium text-gray-700">{item}</div>
                        <div className="text-gray-400">{desc}</div>
                      </div>
                      <span className="text-blue-700 font-semibold whitespace-nowrap">até {pts} pts</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Formulário */}
              <div>
                <p className="font-medium text-gray-700 mb-2">
                  📝 Formulário manual
                  <span className="text-xs font-normal text-gray-400 ml-1">(até 35 pts)</span>
                </p>
                <div className="bg-gray-50 rounded-lg text-xs">
                  <div className="px-3 py-2 border-b border-gray-100">
                    <div className="font-medium text-gray-700 mb-1">
                      Faixa de renda
                      <span className="text-blue-700 font-semibold float-right">até 20 pts</span>
                    </div>
                    <div className="text-gray-400 space-y-0.5">
                      {[
                        ['Até 1 salário mínimo',       '4'],
                        ['1 a 3 salários mínimos',     '10'],
                        ['3 a 5 salários mínimos',     '17'],
                        ['Acima de 5 salários mínimos','20'],
                      ].map(([label, pts]) => (
                        <div key={label} className="flex justify-between">
                          <span>{label}</span><span>{pts} pts</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="px-3 py-2">
                    <div className="font-medium text-gray-700 mb-1">
                      Tempo como cliente
                      <span className="text-blue-700 font-semibold float-right">até 15 pts</span>
                    </div>
                    <div className="text-gray-400 space-y-0.5">
                      {[
                        ['Primeira compra',    '0'],
                        ['Menos de 1 ano',     '5'],
                        ['1 a 3 anos',         '10'],
                        ['Mais de 3 anos',     '15'],
                      ].map(([label, pts]) => (
                        <div key={label} className="flex justify-between">
                          <span>{label}</span><span>{pts} pts</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Entrada */}
              <div>
                <p className="font-medium text-gray-700 mb-2">
                  💰 Valor de entrada
                  <span className="text-xs font-normal text-gray-400 ml-1">(até +20 pts bônus)</span>
                </p>
                <div className="bg-gray-50 rounded-lg text-xs">
                  <div className="px-3 py-2">
                    <div className="text-gray-400 space-y-0.5">
                      {[
                        ['5% a 9% do valor',    '+2'],
                        ['10% a 19% do valor',  '+5'],
                        ['20% a 29% do valor',  '+10'],
                        ['30% a 49% do valor',  '+15'],
                        ['50% ou mais',         '+20'],
                      ].map(([label, pts]) => (
                        <div key={label} className="flex justify-between">
                          <span>{label}</span><span className="text-teal-700 font-semibold">{pts} pts</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-gray-400 mt-2">A entrada reduz o risco e é somada diretamente ao score total (máx. 100 pts).</p>
                  </div>
                </div>
              </div>

              {/* Cliente sem histórico */}
              <div>
                <p className="font-medium text-gray-700 mb-2">🆕 Cliente sem histórico no ERP</p>
                <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-xs text-gray-600 space-y-1">
                  <div>Sem compras a prazo anteriores, os 65 pts do histórico passam para renda e entrada:</div>
                  <div><strong>Renda (até 50):</strong> menos de 1 SM 5 · 1–3 SM 20 · 3–5 SM 35 · 5+ SM 50</div>
                  <div><strong>Tempo como cliente:</strong> igual (até 15)</div>
                  <div><strong>Entrada (até 35):</strong> 5% 3 · 10% 8 · 20% 15 · 30% 25 · 50% 35</div>
                </div>
              </div>

              {/* Resultado final */}
              <div>
                <p className="font-medium text-gray-700 mb-2">🏆 Resultado final (score com entrada)</p>
                <div className="bg-gray-50 rounded-lg divide-y divide-gray-100 text-xs">

                  <div className="flex items-start justify-between px-3 py-2 gap-2">
                    <div>
                      <div className="text-gray-500 font-medium">70 – 100 pts</div>
                    </div>
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 whitespace-nowrap">
                      ✅ Aprovado
                    </span>
                  </div>

                  <div className="flex items-start justify-between px-3 py-2 gap-2">
                    <div>
                      <div className="text-gray-500 font-medium">40 – 69 pts</div>
                      <div className="text-gray-400 mt-0.5">Uma boa entrada pode elevar para Aprovado</div>
                    </div>
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 whitespace-nowrap">
                      ⚠ Aprovado c/ ressalvas
                    </span>
                  </div>

                  <div className="flex items-start justify-between px-3 py-2 gap-2">
                    <div>
                      <div className="text-gray-500 font-medium">0 – 39 pts</div>
                      <div className="text-gray-400 mt-0.5">Mesmo com entrada máxima, permanece reprovado</div>
                    </div>
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 whitespace-nowrap">
                      ❌ Reprovado
                    </span>
                  </div>

                </div>
                <p className="text-xs text-gray-400 mt-2">Percentual de entrada calculado sobre o valor total solicitado.</p>
              </div>

            </div>

            <div className="mt-5 flex justify-end">
              <button onClick={() => setModalPontos(false)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal confirmação WhatsApp ── */}
      {modalZap && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 relative">
            <button onClick={() => {
              if (pollingRef.current) clearInterval(pollingRef.current);
              setModalZap(false);
            }}
            
            className="absolute top-3 right-4 text-gray-400 hover:text-gray-700 text-xl font-bold">×</button>

            <h2 className="text-base font-semibold text-gray-900 mb-1">
              Confirmação pelo WhatsApp
            </h2>
            <p className="text-xs text-gray-500 mb-4">
              Envie a mensagem abaixo ao titular para obter a confirmação de compra.
            </p>

            {/* Telefone */}
            <div className="mb-3">
              <label className="text-xs text-gray-500 block mb-1">Telefone do cliente</label>
              <input
                type="text"
                value={zapTelefone}
                onChange={e => setZapTelefone(e.target.value.replace(/\D/g, ''))}
                placeholder="85999999999"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
              />
            </div>

            {/* Prévia da mensagem */}
            <div className="mb-4 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
              <p className="text-xs font-medium text-green-800 mb-2">Prévia da mensagem:</p>
              <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">
                {mensagemZap()}
              </pre>
            </div>

            {zapErro && (
              <p className="text-xs text-red-600 mb-3">{zapErro}</p>
            )}

            {!zapEnviado ? (
              <button
                onClick={enviarZap}
                disabled={zapEnviando || !zapTelefone}
                className="w-full py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2">
                {zapEnviando ? 'Enviando...' : '📱 Enviar mensagem'}
              </button>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="inline-block w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  Aguardando resposta automaticamente...
                </div>
                <p className="text-xs text-gray-500">
                  Após receber a resposta, tire um print da conversa e registre abaixo:
                </p>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Resposta recebida</label>
                  <textarea
                    rows={3}
                    value={zapResposta}
                    onChange={e => setZapResposta(e.target.value)}
                    disabled={zapRespostaGravada}
                    placeholder='Ex: "SIM, confirmo" — ou descreva a resposta recebida'
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400 resize-none disabled:bg-gray-50"
                  />
                </div>
                {!zapRespostaGravada ? (
                  <button
                    onClick={gravarResposta}
                    disabled={zapGravando || !zapResposta.trim()}
                    className="w-full py-2 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 disabled:opacity-50">
                    {zapGravando ? 'Gravando...' : '💾 Gravar resposta'}
                  </button>
                ) : (
                  <div className="flex items-center gap-2 text-blue-700 text-sm font-medium">
                    <span>✅</span> Resposta gravada com sucesso.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Modal imagens de cadastros anteriores ── */}
      {modalImagensPrev && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setModalImagensPrev(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 relative max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}>
            <button onClick={() => setModalImagensPrev(false)}
              className="absolute top-3 right-4 text-gray-400 hover:text-gray-700 text-xl font-bold">×</button>

            <h2 className="text-base font-semibold text-gray-900 mb-1 flex items-center gap-2">
              <Camera size={16} className="text-amber-600" />Imagens de cadastros anteriores
            </h2>
            <p className="text-xs text-gray-500 mb-4">
              {clienteSel?.Nome} · CPF/CNPJ {cpfDigitos(clienteSel)}. Clique na imagem para ver em tamanho real.
              Cada visualização fica registrada (LGPD).
            </p>

            <div className="space-y-4">
              {Object.values(imagensPrev.reduce((grupos, img) => {
                (grupos[img.analise_id] ??= { ...img, imagens: [] }).imagens.push(img);
                return grupos;
              }, {}))
                .sort((a, b) => new Date(b.data) - new Date(a.data))
                .map(g => (
                  <div key={g.analise_id} className="border border-gray-100 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2 text-xs text-gray-500">
                      Análise #{g.analise_id} · {new Date(g.data).toLocaleDateString('pt-BR')}
                      <ResultBadge resultado={g.resultado} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {g.imagens.map(img => (
                        <FotoMiniatura key={img.arquivo} arquivo={img.arquivo}
                          label={LABEL_SLOT[img.slot] ?? img.slot}
                          carregar={apiService.analiseCredito.getFoto} onAbrir={abrirFotoAnterior} />
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {modalLiberacao && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 relative">
            <button onClick={() => setModalLiberacao(false)}
              disabled={liberacaoEnviando}
              className="absolute top-3 right-4 text-gray-400 hover:text-gray-700 text-xl font-bold">×</button>

            <h2 className="text-base font-semibold text-gray-900 mb-1 flex items-center gap-2">
              <AlertTriangle size={16} className="text-red-600" />Forçar liberação
            </h2>
            <p className="text-xs text-gray-500 mb-4">
              Esta análise foi <strong>reprovada</strong>. Informe a justificativa para liberar o crediário mesmo assim.
              A justificativa será registrada e um e-mail de aviso será enviado à equipe.
            </p>

            <div className="mb-3">
              <label className="text-xs text-gray-500 block mb-1">Justificativa da liberação</label>
              <textarea
                rows={4}
                value={motivoLiberacao}
                onChange={e => setMotivoLiberacao(e.target.value)}
                disabled={liberacaoEnviando}
                placeholder="Descreva o motivo da liberação forçada..."
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400 resize-none disabled:bg-gray-50"
              />
            </div>

            {liberacaoErro && (
              <p className="text-xs text-red-600 mb-3">{liberacaoErro}</p>
            )}

            <button
              onClick={confirmarForcarLiberacao}
              disabled={liberacaoEnviando || !motivoLiberacao.trim()}
              className="w-full py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50">
              {liberacaoEnviando ? 'Confirmando...' : 'Confirmar liberação'}
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-medium text-gray-900 mb-1">Análise de Crediário</h1>
          <p className="text-sm text-gray-500">Avaliação de capacidade de pagamento antes da venda a prazo</p>
        </div>
        <button
          onClick={() => navigate('/historico-analises')}
          className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
        >
          <FileText size={15} />Consultar análises
        </button>
      </div>

      {/* ── Scorecard acumulativo — aparece a partir do histórico ── */}
      {etapa !== 'busca' && (
        <div className={`mb-5 rounded-xl border-2 px-5 py-4 flex items-center gap-5 transition-all
            ${['formulario', 'resultado'].includes(etapa) && form.valor_solicitado
              ? resultadoComEntrada() === 'aprovado' ? 'border-green-400 bg-green-50'
                : resultadoComEntrada() === 'aprovado_ressalvas' ? 'border-amber-400 bg-amber-50'
                : 'border-red-300 bg-red-50'
              : 'border-blue-200 bg-blue-50'}`}>

          {/* Círculo de score */}
          <div className={`w-16 h-16 rounded-full border-4 flex flex-col items-center justify-center flex-shrink-0
            ${(!form.valor_solicitado && etapa === 'formulario')
              ? 'border-blue-300'
              : !form.valor_solicitado ? 'border-blue-300'
              : resultadoComEntrada() === 'aprovado' ? 'border-green-500'
              : resultadoComEntrada() === 'aprovado_ressalvas' ? 'border-amber-500'
              : 'border-red-400'}`}>
            <span className={`text-xl font-bold leading-none ${(!form.valor_solicitado && etapa === 'formulario') ? 'text-blue-500' : resultadoComEntrada() === 'aprovado' ? 'text-green-700' : resultadoComEntrada() === 'aprovado_ressalvas' ? 'text-amber-700' : 'text-red-700'}`}>
              {etapa === 'historico' && loadingHist ? '…' : scoreTotal()}
            </span>
            <span className="text-xs text-gray-400">/100</span>
          </div>

          {/* Barras */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2.5">
              <TrendingUp size={13} className="text-gray-400 flex-shrink-0" />
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">pontuação acumulada</span>
            </div>

            <div className="space-y-1.5">

              {/* Histórico ERP — sempre visível a partir do histórico */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-28 flex-shrink-0">Histórico ERP</span>
                <div className="flex-1">
                  <ScoreBar
                    value={historico && !historico.erro ? historico.pontuacao : 0}
                    max={65}
                    color={
                      etapa === 'historico' && loadingHist ? 'bg-gray-300' :
                      historico && !historico.erro && historico.pontuacao > 0 ? 'bg-blue-400' : 'bg-gray-200'
                    }
                  />
                </div>
                <span className="text-xs font-medium text-gray-600 w-12 text-right">
                  {etapa === 'historico' && loadingHist
                    ? '…'
                    : historico && !historico.erro && semHistorico
                      ? 'sem hist.'
                      : historico && !historico.erro
                        ? `${historico.pontuacao}/65`
                        : '0/65'
                  }
                </span>
              </div>

              {/* SPC — aparece a partir da etapa spc */}
              {['spc', 'formulario', 'resultado'].includes(etapa) && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-28 flex-shrink-0">SPC / Serasa</span>
                  <div className="flex-1">
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          spc.situacao === 'positivo'   ? 'bg-green-400' :
                          spc.situacao === 'negativado' ? 'bg-red-400'   : 'bg-gray-300'
                        }`}
                        style={{ width: spc.situacao === 'nao_consultado' ? '8%' : '100%' }}
                      />
                    </div>
                  </div>
                  <span className="text-xs font-medium text-gray-600 w-12 text-right">
                    {spc.situacao === 'positivo'   ? '✓ OK'  :
                     spc.situacao === 'negativado' ? '⚠ Neg' : 'N/C'}
                  </span>
                </div>
              )}

              {/* Formulário — aparece a partir da etapa formulario */}
              {['formulario', 'resultado'].includes(etapa) && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-28 flex-shrink-0">Formulário</span>
                  <div className="flex-1">
                    <ScoreBar
                      value={calcFormScore()}
                      max={maxForm}
                      color="bg-purple-400"
                    />
                  </div>
                  <span className="text-xs font-medium text-gray-600 w-12 text-right">
                    {calcFormScore()}/{maxForm}
                  </span>
                </div>
              )}

              {/* Entrada — aparece quando há valor de entrada com bônus */}
              {['formulario', 'resultado'].includes(etapa) && calcEntradaScore() > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-28 flex-shrink-0">Entrada</span>
                  <div className="flex-1">
                    <ScoreBar value={calcEntradaScore()} max={maxEntrada} color="bg-teal-400" />
                  </div>
                  <span className="text-xs font-medium text-teal-700 w-12 text-right">
                    +{calcEntradaScore()}
                  </span>
                </div>
              )}

            </div>
          </div>

        {/* Veredito */}
        <div className="text-right flex-shrink-0 min-w-[80px]">
          {['formulario', 'resultado'].includes(etapa) ? (
            <div className={`text-sm font-semibold ${!form.valor_solicitado && etapa === 'formulario' ? 'text-blue-400' : resultadoComEntrada() === 'aprovado' ? 'text-green-700' : resultadoComEntrada() === 'aprovado_ressalvas' ? 'text-amber-700' : 'text-red-700'}`}>
               {!form.valor_solicitado && etapa === 'formulario'
                ? 'Em análise'
                : resultadoComEntrada() === 'aprovado' ? 'Aprovado'
                : resultadoComEntrada() === 'aprovado_ressalvas' ? 'C/ ressalvas'
                : 'Reprovado'}
            </div>
          ) : (
            <div className="text-sm font-semibold text-blue-600">
              Em análise
            </div>
          )}
          <div className="text-xs text-gray-400 mt-0.5">
            {etapa === 'historico' && loadingHist ? 'calculando...' :
            etapa === 'historico'  ? 'parcial' :
            etapa === 'spc'        ? 'parcial' :
            etapa === 'formulario' && !form.valor_solicitado ? 'informe o valor' :
            etapa === 'formulario' ? 'preview' : 'final'}
          </div>

        </div>
      </div>
      )}
      
      {/* Stepper */}
      <div className="flex gap-2 mb-6">
        {steps.map((s, i) => (
          <div key={s.key} className={`flex-1 text-center py-1.5 rounded text-xs font-medium border
            ${i < stepIdx  ? 'bg-green-50 border-green-300 text-green-800' : ''}
            ${i === stepIdx ? 'bg-blue-50 border-blue-400 text-blue-800' : ''}
            ${i > stepIdx  ? 'bg-gray-50 border-gray-200 text-gray-400' : ''}
          `}>{s.label}</div>
        ))}
      </div>

      {/* ── ETAPA 1: BUSCA ── */}
       {etapa === 'busca' && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">buscar cliente</p>
            <button onClick={() => setModalPontos(true)}
              className="flex items-center gap-1.5 text-xs text-blue-600 border border-blue-200 rounded-lg px-3 py-1.5 hover:bg-blue-50 transition-colors">
              <Star size={12} />
              Como funciona a pontuação?
            </button>
          </div>

          {/* Abas */}
          <div className="flex gap-1 mb-4 border-b border-gray-200">
            <button
              onClick={() => { setModoManual(false); setErroBusca(''); }}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors
                ${!modoManual ? 'border-blue-500 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              Buscar no sistema
            </button>
            <button
              onClick={() => setModoManual(true)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors
                ${modoManual ? 'border-blue-500 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              Cliente não cadastrado
            </button>
          </div>

          {/* Aba 1: busca ERP */}
          {!modoManual && (
            <>
              <form onSubmit={buscarClientes} className="flex gap-2 mb-4">
                <input
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
                  placeholder="Nome, código ou CPF do cliente"
                  value={buscaTermo}
                  onChange={e => setBuscaTermo(e.target.value)}
                />
                <button type="submit" disabled={loadingBusca}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">
                  <Search size={15} />{loadingBusca ? 'Buscando...' : 'Buscar'}
                </button>
              </form>

              {erroBusca && (
                <div className="mb-3">
                  <p className="text-sm text-red-600 mb-2">{erroBusca}</p>
                  <button onClick={() => setModoManual(true)}
                    className="text-xs text-blue-600 underline hover:text-blue-800">
                    Cliente não encontrado? Preencher manualmente →
                  </button>
                </div>
              )}

              {clientes.length > 0 && (
                <table className="w-full text-sm border-separate border-spacing-0">
                  <thead>
                    <tr className="text-xs text-gray-500">
                      <th className="text-left pb-2 pl-1">Nome</th>
                      <th className="text-left pb-2">CPF/CNPJ</th>
                      <th className="text-left pb-2">Código</th>
                      <th className="text-left pb-2">Fone</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {clientes.slice(0, 10).map(c => (
                      <tr key={c.Ordem} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="py-2 pl-1 font-medium">{c.Nome}</td>
                        <td className="py-2 text-gray-500 text-xs">{c.CPF || c.CNPJ || '—'}</td>
                        <td className="py-2 text-gray-500">{c.Codigo}</td>
                        <td className="py-2 text-gray-500">{c.Fone_1 || '—'}</td>
                        <td className="py-2">
                          <button onClick={() => selecionarCliente(c)}
                            className="text-xs px-3 py-1 border border-gray-200 rounded hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700">
                            Selecionar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}

          {/* Aba 2: cliente manual */}
          {modoManual && (
            <div className="space-y-3">
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Cliente não localizado no sistema. A análise será feita sem histórico do ERP — pontuação baseada apenas no formulário.
              </div>

              <div>
                <label className="text-xs text-gray-500 block mb-1">Nome completo *</label>
                <input type="text" value={dadosManuais.nome}
                  onChange={e => setDadosManuais(d => ({ ...d, nome: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
                  placeholder="Nome do cliente" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">
                    CPF *
                    {verificandoCpf && <span className="ml-2 text-gray-400">verificando...</span>}
                  </label>
                  <input type="text" value={dadosManuais.cpf}
                    onChange={e => { setDadosManuais(d => ({ ...d, cpf: e.target.value })); setCpfJaCadastrado(null); }}
                    onBlur={e => verificarCpfExistente(e.target.value)}
                    className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none ${cpfJaCadastrado ? 'border-red-400 bg-red-50 focus:border-red-400' : 'border-gray-200 focus:border-blue-400'}`}
                    placeholder="000.000.000-00" />
                  {cpfJaCadastrado && (
                    <div className="mt-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-2.5 py-2 leading-snug">
                      <strong>{cpfJaCadastrado.Nome}</strong> já possui cadastro no sistema (Cód. {cpfJaCadastrado.Codigo}).
                      <button
                        type="button"
                        onClick={() => { setModoManual(false); setBuscaTermo(dadosManuais.cpf); setCpfJaCadastrado(null); setErroBusca(''); }}
                        className="block mt-1 text-red-800 underline hover:text-red-900 font-medium">
                        Buscar este cliente na aba "Buscar no sistema" →
                      </button>
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Telefone *</label>
                  <input type="text" value={dadosManuais.fone}
                    onChange={e => setDadosManuais(d => ({ ...d, fone: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
                    placeholder="(00) 00000-0000" />
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500 block mb-1">Endereço *</label>
                <input type="text" value={dadosManuais.endereco}
                  onChange={e => setDadosManuais(d => ({ ...d, endereco: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
                  placeholder="Rua, número, bairro" />
              </div>

              <div className="grid grid-cols-[1fr_80px] gap-3">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Cidade *</label>
                  <input type="text" value={dadosManuais.cidade}
                    onChange={e => setDadosManuais(d => ({ ...d, cidade: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
                    placeholder="Cidade" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">UF *</label>
                  <input type="text" value={dadosManuais.uf} maxLength={2}
                    onChange={e => setDadosManuais(d => ({ ...d, uf: e.target.value.toUpperCase() }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm uppercase focus:outline-none focus:border-blue-400"
                    placeholder="CE" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Nome da mãe <span className="text-gray-400">(opcional)</span></label>
                  <input type="text" value={dadosManuais.mae}
                    onChange={e => setDadosManuais(d => ({ ...d, mae: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
                    placeholder="Nome completo da mãe" />
                </div>
              </div>

              <div className="flex justify-end pt-1">
                <button onClick={confirmarClienteManual}
                  disabled={!!cpfJaCadastrado || verificandoCpf}
                  className="px-4 py-2 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed">
                  Continuar com este cliente →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── ETAPA 2: HISTÓRICO ── */}
      {etapa === 'historico' && (
        <div className="space-y-4">
          {/* Card cliente */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-medium text-sm flex-shrink-0">
              {clienteSel?.Nome?.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate">{clienteSel?.Nome}</div>
              <div className="text-xs text-gray-500">
                {clienteSel?._manual
                  ? `CPF: ${clienteSel.CPF || 'não informado'} · ${clienteSel.Fone_1 || 'sem telefone'}`
                  : `Cód. ${clienteSel?.Codigo} · ${clienteSel?.Fone_1 || 'sem telefone'}`
                }
              </div>
            </div>
            {historico?.cliente_fiel && (
              <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded font-medium">
                <Star size={11} fill="currentColor" />Cliente fiel
              </span>
            )}
          </div>

          {/* Aviso: imagens de cadastros anteriores */}
          {imagensPrev.length > 0 && (
            <div className="bg-amber-50 border border-amber-300 rounded-xl px-5 py-3 flex items-center gap-3">
              <Camera size={18} className="text-amber-600 flex-shrink-0" />
              <p className="flex-1 text-sm text-amber-800">
                Este cliente tem <strong>{imagensPrev.length} imagem(ns)</strong> de cadastros anteriores
                (última em {new Date(imagensPrev[0].data).toLocaleDateString('pt-BR')}).
              </p>
              <button onClick={() => setModalImagensPrev(true)}
                className="px-3 py-1.5 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700">
                Visualizar
              </button>
            </div>
          )}

          {/* Histórico ERP */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-4">histórico automático — ERP</p>
            {loadingHist ? (
              <p className="text-sm text-gray-400 text-center py-6">Carregando histórico...</p>
            ) : historico?.erro ? (
              <p className="text-sm text-red-500">Erro ao carregar histórico do ERP.</p>
            ) : historico?.total_parcelas === 0 ? (
              <div className="text-sm text-amber-700 bg-amber-50 rounded-lg p-3">
                Nenhuma compra a prazo anterior. Este é um cliente novo no crediário —
                a pontuação vai considerar <strong>renda (até 50 pts)</strong> e <strong>entrada (até 35 pts)</strong> no lugar do histórico.
              </div>
            ) : (
              <>
                <div className="space-y-2 mb-4">
                  {[
                    ['Total de parcelas no crediário', historico.total_parcelas],
                    ['Pagas em dia', `${historico.pagas_em_dia} (${Math.round(historico.pagas_em_dia / historico.total_parcelas * 100)}%)`, 'green'],
                    ['Atraso leve (1–15 dias)', historico.atraso_leve, historico.atraso_leve > 0 ? 'amber' : null],
                    ['Atraso grave (+30 dias)',  historico.atraso_grave, historico.atraso_grave > 0 ? 'red' : null],
                    ['Parcelas em aberto agora', historico.parcelas_abertas, historico.parcelas_abertas > 0 ? 'red' : 'green'],
                    ['Maior atraso registrado',  `${historico.maior_atraso} dias`, historico.maior_atraso > 30 ? 'red' : null],
                    ['Média de dias de atraso',  `${historico.media_atraso} dias`],
                  ].map(([label, value, cor]) => (
                    <div key={label} className="flex justify-between items-center text-sm py-1.5 border-b border-gray-100 last:border-0">
                      <span className="text-gray-500">{label}</span>
                      <span className={`font-medium ${cor === 'green' ? 'text-green-700' : cor === 'amber' ? 'text-amber-700' : cor === 'red' ? 'text-red-600' : 'text-gray-900'}`}>
                        {value}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-3 pt-1">
                  <div className="flex-1">
                    <div className="text-xs text-gray-500 mb-1">Pontuação do histórico</div>
                    <ScoreBar value={historico.pontuacao} max={65} color="bg-blue-500" />
                  </div>
                  <div className={`text-lg font-medium ${scoreColor(historico.pontuacao * (100/65))}`}>
                    {historico.pontuacao}<span className="text-xs text-gray-400">/65</span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Análises anteriores */}
          {analisesPrev.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <button className="w-full flex justify-between items-center px-5 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
                onClick={() => setMostrarPrev(v => !v)}>
                Análises anteriores ({analisesPrev.length})
                {mostrarPrev ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
              </button>
              {mostrarPrev && (
                <div className="px-5 pb-4 space-y-2">
                  {analisesPrev.map(a => (
                    <div key={a.id} className="flex justify-between items-center text-xs py-1.5 border-b border-gray-100 last:border-0">
                      <span className="text-gray-500">{new Date(a.created_at).toLocaleDateString('pt-BR')}</span>
                      <span className="text-gray-700">R$ {Number(a.valor_aprovado).toFixed(2).replace('.', ',')} em {a.num_parcelas}x</span>
                      <ResultBadge resultado={a.resultado} />
                      <span className="text-gray-400">{a.nome_operador}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Bloqueio por atraso crítico */}
          {historico?.atraso_critico && (
            <div className="bg-red-50 border border-red-300 rounded-xl px-5 py-4 flex items-start gap-3">
              <span className="text-red-500 text-lg mt-0.5">🚫</span>
              <div>
                <p className="text-sm font-semibold text-red-700">Cliente bloqueado para crediário</p>
                <p className="text-xs text-red-600 mt-1">
                  Há parcela(s) em aberto com mais de 45 dias de atraso. O crediário será reprovado
                  independentemente da pontuação. Regularize a situação antes de prosseguir.
                </p>
              </div>
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <button onClick={() => setEtapa('busca')}
              className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">
              ← Voltar
            </button>
            <button onClick={() => setEtapa('spc')} disabled={loadingHist}
              className="px-4 py-2 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100 disabled:opacity-50">
              Continuar →
            </button>
          </div>
        </div>
      )}

     
      {/* ── ETAPA 3: SPC ── */}
      {etapa === 'spc' && (
        <div className="space-y-4">

          {/* Situação SPC */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">consulta spc / serasa</p>
            <p className="text-xs text-gray-400 mb-4">Preencha com o resultado da consulta manual no sistema externo. Campo opcional.</p>

            <div className="grid grid-cols-3 gap-3 mb-4">
              {[
                { value: 'nao_consultado', label: 'Não consultado', color: 'gray'  },
                { value: 'positivo',       label: 'Nada consta',    color: 'green' },
                { value: 'negativado',     label: 'Negativado',     color: 'red'   },
              ].map(op => (
                <button key={op.value}
                  onClick={() => setSpc(s => ({ ...s, situacao: op.value }))}
                  className={`py-3 rounded-lg border-2 text-sm font-medium transition-colors
                    ${spc.situacao === op.value
                      ? op.color === 'green' ? 'border-green-400 bg-green-50 text-green-800'
                      : op.color === 'red'   ? 'border-red-400 bg-red-50 text-red-700'
                      :                        'border-gray-400 bg-gray-50 text-gray-700'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}>
                  {op.label}
                </button>
              ))}
            </div>

            <div>
              <label className="text-xs text-gray-500 block mb-1">Observações da consulta</label>
              <textarea rows={2} value={spc.observacoes}
                onChange={e => setSpc(s => ({ ...s, observacoes: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400 resize-none"
                placeholder="Ex: 1 restrição de R$ 450 em aberto..." />
            </div>
          </div>

          {/* Renda */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-4">renda mensal</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Valor da renda (R$)</label>
                <input type="number" min="0" step="0.01" value={spc.renda_mensal}
                  onChange={e => setSpc(s => ({ ...s, renda_mensal: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
                  placeholder="0,00" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Fonte</label>
                <select value={spc.fonte_renda}
                  onChange={e => setSpc(s => ({ ...s, fonte_renda: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400">
                  <option value="declarado">Declarado pelo cliente</option>
                  <option value="documentado">Comprovado por documento</option>
                </select>
              </div>
            </div>
          </div>

          {/* Comprovante de renda — identidade agora é fotografada na etapa 6 (webcam) */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">comprovante de renda</p>
            <p className="text-xs text-gray-400 mb-4">
              Opcional. JPG, PNG ou PDF até 10 MB. O documento de identidade é fotografado na etapa 6.
            </p>

            <div>
              <label className={`flex items-center gap-3 border-2 border-dashed rounded-lg px-4 py-3 cursor-pointer transition-colors
                ${spc.doc_comprovante ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <input type="file" accept=".jpg,.jpeg,.png,.pdf" className="hidden"
                  onChange={e => setSpc(s => ({ ...s, doc_comprovante: e.target.files[0] ?? null }))} />
                <span className="text-sm text-gray-500 flex-1">
                  {spc.doc_comprovante ? `✅ ${spc.doc_comprovante.name}` : 'Clique para anexar'}
                </span>
                {spc.doc_comprovante && (
                  <button onClick={e => { e.preventDefault(); setSpc(s => ({ ...s, doc_comprovante: null })); }}
                    className="text-xs text-red-500 hover:text-red-700">remover</button>
                )}
              </label>
            </div>
          </div>

          <label className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer
            ${cienciaLgpd ? 'bg-green-50 border-green-300' : 'bg-amber-50 border-amber-300'}`}>
            <input type="checkbox" checked={cienciaLgpd}
              onChange={e => setCienciaLgpd(e.target.checked)}
              className="mt-0.5 h-4 w-4" />
            <span className="text-sm text-gray-800">
              <strong>Informei o cliente</strong> que seus dados, documentos e fotos serão usados somente
              para análise de crédito, identificação, prevenção a fraudes e cobrança deste contrato,
              guardados por até 6 anos, e que ele pode pedir acesso, correção ou revisão da decisão na loja.
              <span className="block text-xs text-gray-500 mt-1">Obrigatório (LGPD, art. 9º). Fica registrado com seu usuário e a data.</span>
            </span>
          </label>

          <div className="flex gap-2 justify-end">
            <button onClick={() => setEtapa('historico')}
              className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">
              ← Voltar
            </button>
            <button
              onClick={async () => { await fazerUploadDocs(); setEtapa('formulario'); }}
              disabled={uploadandoDocs || !cienciaLgpd}
              title={!cienciaLgpd ? 'Confirme que o cliente foi informado sobre o uso dos dados' : ''}
              className="px-4 py-2 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100 disabled:opacity-50">
              {uploadandoDocs ? 'Enviando comprovante...' : 'Continuar →'}
            </button>
          </div>
        </div>
      )}

      {/* ── ETAPA 4: FORMULÁRIO ── */}
      {etapa === 'formulario' && (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-4">dados do cliente</p>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Renda mensal (R$)</label>
                <input type="number" min="0" step="0.01"
                  value={form.renda_mensal !== '' ? form.renda_mensal : spc.renda_mensal}
                  onChange={e => setForm(f => ({ ...f, renda_mensal: e.target.value }))}
                  placeholder={spc.renda_mensal ? `Sugerido: R$ ${Number(spc.renda_mensal).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : 'Ex: 2.000,00'}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Tempo como cliente da loja</label>
                <select value={form.tempo_cliente} onChange={e => setForm(f => ({ ...f, tempo_cliente: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400">
                  {TEMPO_CLIENTE.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>

            {/* Preview score */}
            {/* Preview score */}
            <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
              <div className="flex-1">
                <div className="text-xs text-gray-500 mb-1">Pontuação do formulário</div>
                <ScoreBar value={calcFormScore()} max={maxForm} color="bg-purple-400" />
              </div>
              <div className="text-base font-medium text-gray-700">{calcFormScore()}<span className="text-xs text-gray-400">/{maxForm}</span></div>
              <div className="w-px h-8 bg-gray-200" />
              <div className="flex-1">
                  <div className="text-xs text-gray-500 mb-1">Score total (preview)</div>
                  {form.valor_solicitado ? (
                    <ScoreBar value={scoreTotal()} max={100}
                      color={scoreTotal() >= 70 ? 'bg-green-500' : scoreTotal() >= 40 ? 'bg-amber-400' : 'bg-red-500'} />
                  ) : (
                    <div className="text-xs text-gray-400 italic mt-1">preencha o valor solicitado</div>
                  )}
              </div>
              <div className={`text-base font-medium ${form.valor_solicitado ? scoreColor(scoreTotal()) : 'text-gray-300'}`}>
                  {form.valor_solicitado ? scoreTotal() : '—'}
                  <span className="text-xs text-gray-400">/100</span>
              </div>
            </div>

            {/* Indicador de impacto da entrada */}
            {(() => {
              const baseScore = (historico?.pontuacao ?? 0) + calcFormScore();
              const semEntrada = baseScore >= 70 ? 'aprovado' : baseScore >= 40 ? 'aprovado_ressalvas' : 'reprovado';
              const comEntrada = resultadoComEntrada();
              const minPct     = entradaMinimaNecessaria();
              const pctAtual   = (form.valor_solicitado > 0 && form.valor_entrada > 0)
                ? Math.round((parseFloat(form.valor_entrada) / parseFloat(form.valor_solicitado)) * 100)
                : 0;
              const entradaAjudou = comEntrada !== semEntrada && pctAtual > 0;
              const ptsBônus = calcEntradaScore();

              if (entradaAjudou) {
                return (
                  <div className="mt-2 flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-xs text-green-800">
                    <span>✅</span>
                    <span>Entrada de <strong>{pctAtual}%</strong> (+{ptsBônus} pts) elevou de <strong>{semEntrada === 'reprovado' ? 'Reprovado' : 'Aprovado c/ ressalvas'}</strong> para <strong>{comEntrada === 'aprovado' ? 'Aprovado' : 'Aprovado c/ ressalvas'}</strong></span>
                  </div>
                );
              }
              if (ptsBônus > 0 && !entradaAjudou && semEntrada !== 'aprovado') {
                return (
                  <div className="mt-2 flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2 text-xs text-teal-800">
                    <span>📈</span>
                    <span>Entrada de <strong>{pctAtual}%</strong> adicionou <strong>+{ptsBônus} pts</strong> ao score — resultado segue <strong>{comEntrada === 'aprovado_ressalvas' ? 'Aprovado c/ ressalvas' : 'Reprovado'}</strong></span>
                  </div>
                );
              }
              if (!entradaAjudou && minPct && pctAtual < minPct && scoreTotal() < 70) {
                const vlrMin = form.valor_solicitado
                  ? `R$ ${(parseFloat(form.valor_solicitado) * minPct / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                  : `${minPct}%`;
                return (
                  <div className="mt-2 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                    <span>💡</span>
                    <span>Entrada mínima de <strong>{minPct}% ({vlrMin})</strong> pode melhorar o resultado desta análise</span>
                  </div>
                );
              }
              return null;
            })()}
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-4">condições da venda</p>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Valor solicitado (R$)</label>
                <input type="number" min="0" step="0.01" value={form.valor_solicitado}
                  onChange={e => setForm(f => ({ ...f, valor_solicitado: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Nº de parcelas</label>
                <input type="number" min="1" max="60" value={form.num_parcelas}
                  onChange={e => setForm(f => ({ ...f, num_parcelas: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Entrada (R$)</label>
                <input type="number" min="0" step="0.01" value={form.valor_entrada}
                  onChange={e => setForm(f => ({ ...f, valor_entrada: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-4">operador responsável</p>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Nome do operador *</label>
                <input type="text" value={form.nome_operador}
                  readOnly
                  className="w-full border border-gray-100 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-700 cursor-default"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Observações</label>
              <textarea rows={3} value={form.observacoes}
                onChange={e => setForm(f => ({ ...f, observacoes: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400 resize-none"
                placeholder="Anotações relevantes sobre a análise..." />
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <button onClick={() => setEtapa('spc')}
              className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">
              ← Voltar
            </button>
            <button onClick={salvar} disabled={salvando}
              className="px-4 py-2 border border-green-400 bg-green-50 text-green-800 rounded-lg text-sm font-medium hover:bg-green-100 disabled:opacity-50">
              {salvando ? 'Salvando...' : 'Finalizar e ver resultado →'}
            </button>
          </div>
        </div>
      )}

      {/* ── ETAPA 5: RESULTADO ── */}
      {etapa === 'resultado' && resultado && (
        <div className="space-y-4">
          <div ref={printRef}>
            {/* Cabeçalho */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4">
              <div className="flex items-start gap-4">
                <div className={`w-16 h-16 rounded-full flex flex-col items-center justify-center border-4 flex-shrink-0
                  ${resultado.resultado === 'aprovado' ? 'border-green-400' : resultado.resultado === 'aprovado_ressalvas' ? 'border-amber-400' : 'border-red-400'}`}>
                  <span className={`text-xl font-semibold ${scoreColor(resultado.score_total)}`}>{resultado.score_total}</span>
                  <span className="text-xs text-gray-400">/100</span>
                </div>
                <div className="flex-1">
                  <h2 className="text-base font-medium mb-1">{clienteSel?.Nome}</h2>
                  <div className="flex flex-wrap gap-2 mb-2">
                    <ResultBadge resultado={resultado.resultado} />
                    {resultado.cliente_fiel && (
                      <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded font-medium">
                        <Star size={11} fill="currentColor" />Cliente fiel
                      </span>
                    )}
                    {spc.situacao === 'negativado' && (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-medium">⚠ Negativado SPC</span>
                    )}
                    {resultado?.atraso_critico && (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-medium">
                        🚫 Bloqueado — atraso +45 dias
                      </span>
                    )}
                    {spc.situacao === 'positivo' && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium">✓ Nada consta SPC</span>
                    )}
                    {resultado.entrada_foi_decisiva && (
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded font-medium">📥 Entrada decisiva ({resultado.percentual_entrada}%)</span>
                    )}
                  </div>
                  <div className={`rounded-lg p-3 border text-sm ${scoreBg(resultado.score_total)}`}>
                    {resultado.resultado === 'aprovado' && !resultado.entrada_foi_decisiva && 'Bom histórico e dados consistentes. Crediário aprovado conforme solicitado.'}
                    {resultado.resultado === 'aprovado' && resultado.entrada_foi_decisiva && `Aprovado com entrada de ${resultado.percentual_entrada}%. Sem a entrada, o resultado seria inferior.`}
                    {resultado.resultado === 'aprovado_ressalvas' && !resultado.entrada_foi_decisiva && 'Pontuação intermediária. Considere solicitar entrada ou reduzir o número de parcelas.'}
                    {resultado.resultado === 'aprovado_ressalvas' && resultado.entrada_foi_decisiva && `Aprovado com ressalvas graças à entrada de ${resultado.percentual_entrada}%. Mantenha atenção ao histórico futuro.`}
                    {resultado.resultado === 'reprovado' && (
                        resultado.atraso_critico
                          ? 'Cliente possui parcela em aberto há mais de 45 dias. Crediário bloqueado independentemente da pontuação.'
                          : 'Pontuação insuficiente. Recomenda-se não conceder o crediário nas condições solicitadas.'
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Resumo SPC/Renda */}
            {(spc.situacao !== 'nao_consultado' || spc.renda_mensal) && (
              <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">dados spc / renda</p>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  {spc.situacao !== 'nao_consultado' && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs text-gray-500 mb-1">Situação SPC</div>
                      <div className={`text-sm font-medium ${spc.situacao === 'positivo' ? 'text-green-700' : 'text-red-700'}`}>
                        {spc.situacao === 'positivo' ? 'Nada consta' : 'Negativado'}
                      </div>
                    </div>
                  )}
                  {spc.renda_mensal && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs text-gray-500 mb-1">Renda mensal</div>
                      <div className="text-sm font-medium">
                        R$ {Number(spc.renda_mensal).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        <span className="text-xs text-gray-400 ml-1">
                          ({spc.fonte_renda === 'documentado' ? 'documentado' : 'declarado'})
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                {spc.observacoes && (
                  <div className="text-xs text-gray-500 bg-gray-50 rounded p-2 mb-2">
                    <span className="font-medium">Obs. SPC: </span>{spc.observacoes}
                  </div>
                )}
                {spc.doc_comprovante_nome && (
                  <div className="text-xs text-gray-500">
                    📎 Comprovante de renda: {spc.doc_comprovante_nome}
                  </div>
                )}
              </div>
            )}

            {/* Resumo da venda */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">resumo da venda</p>
              <div className="grid grid-cols-3 gap-3">
                {[
                  ['Valor aprovado', `R$ ${Number(form.valor_solicitado).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`],
                  ['Parcelas', `${form.num_parcelas}x de R$ ${((Number(form.valor_solicitado) - Number(form.valor_entrada || 0)) / (Number(form.num_parcelas) || 1)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`],
                  ['Entrada', form.valor_entrada > 0 ? `R$ ${Number(form.valor_entrada).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : 'Sem entrada'],
                ].map(([label, val]) => (
                  <div key={label} className="bg-gray-50 rounded-lg p-3 text-center">
                    <div className="text-xs text-gray-500 mb-1">{label}</div>
                    <div className="text-sm font-medium">{val}</div>
                  </div>
                ))}
              </div>
              {form.observacoes && (
                <div className="mt-3 text-xs text-gray-500 bg-gray-50 rounded p-2">
                  <span className="font-medium">Obs.: </span>{form.observacoes}
                </div>
              )}
            </div>

            {/* Assinatura */}
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-4">assinatura — operador responsável</p>
              <div className="flex justify-between items-end">
                <div>
                  <div className="border-t border-gray-900 w-52 mb-1" />
                  <div className="text-xs text-gray-600">{form.nome_operador}</div>
                  <div className="text-xs text-gray-400">Responsável pela análise</div>
                </div>
                <div className="text-xs text-gray-400 text-right">
                  <div>Análise nº {analiseId}</div>
                  <div>{new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</div>
                  <div>{new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <button onClick={resetarAnalise}
              className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">
              Nova análise
            </button>
            <button onClick={imprimir}
              className="flex items-center gap-2 px-4 py-2 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100">
              <Printer size={15} />Imprimir análise
            </button>
            {resultado?.resultado !== 'reprovado' && (
              <button
                onClick={abrirModalZap}
                className="flex items-center gap-2 px-4 py-2 border border-green-400 bg-green-50 text-green-800 rounded-lg text-sm hover:bg-green-100">
                📱 Confirmação WhatsApp
              </button>
            )}
            {(resultado?.resultado !== 'reprovado' || resultado?.liberacao_forcada) && (
              <button onClick={() => setEtapa('fotos')}
                className="flex items-center gap-2 px-4 py-2 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100">
                <Camera size={15} />Próximo: fotos →
              </button>
            )}
            {resultado?.resultado === 'reprovado' && !resultado?.liberacao_forcada && (
              <button onClick={abrirModalLiberacao}
                className="flex items-center gap-2 px-4 py-2 border border-red-300 bg-red-50 text-red-700 rounded-lg text-sm hover:bg-red-100 transition-colors">
                <FileText size={15} />Forçar liberação
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── ETAPA 6: FOTOS ── */}
      {etapa === 'fotos' && resultado && (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">fotos do cliente e documentos</p>
            <p className="text-xs text-gray-400 mb-3">
              {clienteSel?.Nome} · {cpfDigitos(clienteSel)
                ? `pasta do CPF/CNPJ ${cpfDigitos(clienteSel)}`
                : 'cliente sem CPF/CNPJ no cadastro — fotos salvas na pasta desta análise'}
            </p>
            {!cpfDigitos(clienteSel) && (
              <div className="text-xs bg-amber-50 border border-amber-300 text-amber-800 rounded-lg px-3 py-2 mb-3">
                ⚠ Cadastre o CPF do cliente no ERP para que as próximas análises fiquem na mesma pasta.
              </div>
            )}

            <div className="text-xs bg-blue-50 border border-blue-200 text-blue-800 rounded-lg px-3 py-2 mb-3">
              As fotos são <strong>opcionais</strong> — não é obrigatório anexar nenhuma.
              O contrato pode ser impresso sem fotos.
            </div>

            <div className="grid grid-cols-2 gap-3">
              {SLOTS_FOTO.map(slot => {
                const foto = fotos[slot.key];
                return (
                  <div key={slot.key} className="border border-gray-200 rounded-lg p-3">
                    <div className="text-xs font-medium text-gray-700 mb-2">
                      {slot.label}
                      <span className="text-gray-400 font-normal ml-1">(opcional)</span>
                    </div>
                    <div className="bg-gray-50 rounded aspect-video flex items-center justify-center overflow-hidden mb-2">
                      {foto
                        ? <img src={foto.url} alt={slot.label} className="w-full h-full object-cover" />
                        : <Camera size={28} className="text-gray-300" />}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setSlotCaptura(slot.key)}
                        className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg text-xs hover:bg-blue-100">
                        <Camera size={13} />{foto ? 'Refazer' : 'Fotografar'}
                      </button>
                      {foto && (
                        <button onClick={() => removerFoto(slot.key)} disabled={removendoSlot === slot.key}
                          className="flex items-center gap-1.5 px-2 py-1.5 border border-gray-200 text-gray-500 rounded-lg text-xs hover:bg-gray-50 disabled:opacity-50">
                          <Trash2 size={13} />Remover
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <button onClick={() => setEtapa('resultado')}
              className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">
              ← Resultado
            </button>
            <button onClick={resetarAnalise}
              className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">
              Nova análise
            </button>
            <button onClick={imprimirContrato}
              className="flex items-center gap-2 px-4 py-2 border border-green-400 bg-green-50 text-green-800 rounded-lg text-sm font-medium hover:bg-green-100 transition-colors">
              <FileText size={15} />Imprimir contrato
            </button>
          </div>

          {slotCaptura && (
            <CapturaFoto
              titulo={SLOTS_FOTO.find(x => x.key === slotCaptura)?.label}
              onConfirmar={enviarFoto}
              onFechar={() => setSlotCaptura(null)}
            />
          )}
        </div>
      )}
    </div>
  );
}